// src/components/cart/cartDropdown.jsx - FIXED VERSION
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/cartContext';
import { useCartStock } from '../../hooks/useCartStock';

const CartDropdown = () => {
  const { cart, itemCount, totalAmount, removeItem, isAuthenticated } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ✅ Use real-time stock hook
  const { getCurrentStock, hasOutOfStockItems } = useCartStock(cart.items, isAuthenticated);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleRemoveItem = (productId, color, e) => {
    e.preventDefault();
    e.stopPropagation();
    removeItem(productId, color);
  };

  // ✅ Get stock badge with real-time data
  const getStockBadge = (item) => {
    const currentStock = getCurrentStock(item);
    const stock = currentStock.stockAvailable || currentStock.stock || 0;
    
    if (!currentStock.isInStock || stock === 0) {
      return <span className="text-xs bg-red-100 text-red-800 px-1 py-0.5 rounded">Out of Stock</span>;
    }
    if (stock <= 10) {
      return <span className="text-xs bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded">Low Stock</span>;
    }
    return null; // Don't show badge if in stock
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Cart Icon with Badge */}
      <button 
        className="flex flex-col items-center text-gray-700 hover:text-blue-600 relative"
        onClick={toggleDropdown}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        <span className="text-xs mt-1">CART</span>
        {itemCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {itemCount}
          </span>
        )}
        {/* ✅ Show warning indicator if items are out of stock */}
        {!isAuthenticated && hasOutOfStockItems() && (
          <span className="absolute -top-1 -right-1 bg-yellow-500 rounded-full h-3 w-3 border-2 border-white"></span>
        )}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50">
          <div className="py-2 px-4 border-b">
            <h3 className="font-medium">
              {itemCount === 0 ? 'Your Cart is Empty' : `Your Cart (${itemCount} items)`}
            </h3>
            {/* ✅ Show stock warning for guest users */}
            {!isAuthenticated && hasOutOfStockItems() && (
              <p className="text-xs text-yellow-800 mt-1">
                ⚠️ Some items have stock issues
              </p>
            )}
          </div>

          {itemCount > 0 ? (
            <>
              <div className="py-2 px-2 max-h-64 overflow-y-auto">
                {cart.items.map((item) => {
                  const currentStock = getCurrentStock(item);
                  const isOutOfStock = !currentStock.isInStock || currentStock.stockAvailable < item.quantity;
                  
                  return (
                    <div 
                      key={`${item.productId}-${item.color || 'default'}`}
                      className={`py-2 px-2 hover:bg-gray-50 flex items-center border-b ${isOutOfStock ? 'bg-red-50' : ''}`}
                    >
                      <Link 
                        to={`/product/${item.productId}`}
                        className="flex items-center flex-1"
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                          <img
                            src={item.thumbnailUrl || item.imageUrl || '/images/placeholder.png'}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                          <div className="flex flex-col text-xs text-gray-500">
                            {item.manufacturer && (
                              <span className="truncate">{item.manufacturer}</span>
                            )}
                            <span>
                              {item.color && `${item.color} | `}
                              Qty: {item.quantity}
                            </span>
                            {/* ✅ Show stock badge */}
                            {getStockBadge(item)}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm font-bold">
                              ₹{((currentStock.price || item.price) * item.quantity).toLocaleString()}
                            </p>
                            {item.isBulkItem && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded">
                                Bulk
                              </span>
                            )}
                          </div>
                          {/* ✅ Out of stock warning */}
                          {isOutOfStock && (
                            <p className="text-xs text-red-600 mt-1">
                              {!currentStock.isInStock ? 'Out of stock' : `Only ${currentStock.stockAvailable} available`}
                            </p>
                          )}
                        </div>
                      </Link>
                      
                      <button 
                        className="ml-2 text-gray-400 hover:text-red-500"
                        onClick={(e) => handleRemoveItem(item.productId, item.color, e)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="py-2 px-4 border-t">
                <div className="space-y-1 text-sm">
                  {cart.bulkDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Bulk Discount:</span>
                      <span>-₹{cart.bulkDiscount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium">
                    <span>Total:</span>
                    <span>₹{(totalAmount || cart.subtotal || 0).toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="mt-2 space-y-2">
                  <Link
                    to="/cart"
                    className="block w-full text-center bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    View Cart
                  </Link>
                  <Link
                    to="/checkout"
                    className="block w-full text-center border border-blue-600 text-blue-600 py-2 rounded hover:bg-blue-50 transition"
                    onClick={() => setIsOpen(false)}
                  >
                    Checkout
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="py-8 px-4 text-center">
              <p className="text-gray-500 mb-4">Your shopping cart is empty.</p>
              <Link
                to="/products"
                className="block w-full text-center bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
                onClick={() => setIsOpen(false)}
              >
                Start Shopping
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartDropdown;